import React, { useRef } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { Typography } from "@mui/material";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import AutoSuggest from "../../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../../components/Form/Button";
import Select from "../../../../../../components/Form/Select";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "../../../../../../components/MoreOptionsComponent/MoreOptions";
import { LOCATION_STATUS } from "../../../../../../constants";
import { Job } from "../../../../../../models/job";
import { Filters } from "../../../../../../models/shipeezi.table";
import { StorageBox } from "../../../../../../models/storage.box";
import { MapCell } from "../../../../../../models/warehouse.mapcell";
import { LocationStatus } from "../../../../../../util/enums";
import {
  OrderView,
  OrderViewComponentHandler,
} from "../../../Warehousing/Transshipments/components/OrderViewComponent";
import BadgeLocationStatus from "../components/BadgeLocationStatus";
import { useLocationsActions } from "./hooks/useActions";
import {
  DownloadButton,
  EmptyDiv,
  ExportCSVButton,
  LeftActionsGrid,
  OrderOption,
  RightActionsGrid,
} from "./styles";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { DocumentDownload } from "iconsax-react";
import { colors } from "@zapalacorp/tokens";
import { handleGenerateMovementsAndLocationStatusCSV } from "Util/generateCsv";
import { useAppState } from "Context/StateContext";

const downloadSvg = require("Assets/img/document-download-outline.svg");

export interface DateOption {
  name: string;
  column: string;
}

const getMapCellLabel = (mapCell: MapCell) => {
  const mapCellName = mapCell?.name;
  const sectionName = mapCell?.map?.section?.name;
  const mapName = mapCell?.map?.name;

  return `${mapCellName ?? ""} (${sectionName} - ${mapName})`;
};

export const LocationsStatusReportsScreen = () => {
  const {
    getLocations,
    getMapCells,
    getStorageBoxTypesList,
    getWarehousesList,
    locationsFilters,
    locationsReports,
    mapCellSelected,
    page,
    pageSize,
    selectedWarehouseId,
    setFilters,
    setMapCellSelected,
    setPage,
    setPageSize,
    setSelectedWarehouseId,
    storageBoxTypesList,
    total,
    useExportLocationStatusCSVData,
    useGenerateLocationsReport,
    warehousesList,
  } = useLocationsActions();

  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const ordersNumberMenu = useRef<MoreOptionsComponentHandles>(null);
  const orderViewComponentRef = useRef<OrderViewComponentHandler>(null);

  const { isFetching: loadingLocations } = getLocations();
  const { isFetching: isFetchingWarehouses } = getWarehousesList();
  const { mutate: generateReport, isLoading: isGeneratingReport } = useGenerateLocationsReport();

  const { mutate: exportLocationStatusData, isLoading: isExportingCSV } =
    useExportLocationStatusCSVData((data) =>
      handleGenerateMovementsAndLocationStatusCSV({
        data,
        reportName: "Location_status_report",
        companyName: currentUser?.company.name || "",
      })
    );

  getStorageBoxTypesList();

  const renderDownloadReportButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
        spacing={2}
      >
        <Grid item>
          <DownloadButton
            onClick={() => generateReport(locationsFilters)}
            customColor={Color.SECONDARY}
            disabled={
              !selectedWarehouseId ||
              isGeneratingReport ||
              loadingLocations ||
              locationsReports.length === 0
            }
            loading={isGeneratingReport}
            startIcon={<img src={downloadSvg} />}
          >
            {translate("downloadReport")}
          </DownloadButton>
        </Grid>
      </RightActionsGrid>
    );
  };

  const renderExportCSVButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
        spacing={2}
      >
        <DefaultTooltip title={translate("exportCSV")} placement="top">
          <Grid item>
            <ExportCSVButton
              loading={isExportingCSV}
              customColor={Color.SECONDARY}
              disabled={isExportingCSV || loadingLocations || locationsReports.length === 0}
              onClick={() => exportLocationStatusData()}
              startIcon={<DocumentDownload size={20} color={colors.white} />}
            >
              {translate("exportCSV")}
            </ExportCSVButton>
          </Grid>
        </DefaultTooltip>
      </RightActionsGrid>
    );
  };

  const renderWarehouseSelect = () => {
    return (
      <LeftActionsGrid container item direction="row">
        <Select
          placeholder={`${translate("select")}...`}
          label={translate("warehouse")}
          disabled={isFetchingWarehouses || isGeneratingReport}
          value={selectedWarehouseId}
          onChange={(event: SelectChangeEvent<any>) => {
            const warehouseId = Number(event.target.value);

            const foundWarehouse = warehousesList?.find(
              (warehouse) => warehouse?.id === warehouseId
            );

            if (foundWarehouse && foundWarehouse.id) {
              setPage(0);
              setSelectedWarehouseId(foundWarehouse.id);
            }
          }}
        >
          {warehousesList?.map((warehouse) => (
            <MenuItem value={warehouse?.id} key={warehouse?.id}>
              {warehouse?.name}
            </MenuItem>
          ))}
        </Select>
      </LeftActionsGrid>
    );
  };

  const renderLocationSelect = () => {
    return (
      <LeftActionsGrid container item direction="row">
        <AutoSuggest
          label={translate("location")}
          placeholder={translate("search")}
          noOptionsText={translate("noResults")}
          getOptionLabel={(option: MapCell) => {
            if (!option?.name) return "";

            return getMapCellLabel(option);
          }}
          getOptionSelected={(option: MapCell, value: MapCell) => option?.id === value?.id}
          filterOptions={(options) => options}
          inputValue={mapCellSelected?.name}
          value={mapCellSelected || null}
          clearOptionsOnEscape
          disabled={!selectedWarehouseId || isGeneratingReport}
          onChange={(value: MapCell) => {
            setPage(0);
            setMapCellSelected(value);
          }}
          onSearch={(value) => {
            return getMapCells(selectedWarehouseId, value);
          }}
          dataCy="auto-suggest-move-to-location"
        />
      </LeftActionsGrid>
    );
  };

  const getColumns = () => {
    const columns: Column[] = [
      {
        name: "mapCell",
        hideSearch: true,
        title: translate("warehouse"),
        formatText: (mapCell: MapCell) => mapCell?.map?.warehouse?.name ?? "",
      },
      {
        name: "storageBox",
        hideSearch: true,
        title: translate("storageBoxType"),
        formatText: (storageBox: StorageBox) => storageBox?.storageBoxType?.name ?? "",
      },
      {
        name: "storageBox",
        hideSearch: true,
        title: `${translate("storageBox")} #`,
        formatText: (storageBox: StorageBox) => storageBox?.number ?? "",
      },
      {
        name: "mapCell",
        hideSearch: true,
        title: translate("location"),
        formatText: (mapCell: MapCell) => getMapCellLabel(mapCell),
      },
      {
        name: "storageBox",
        hideSearch: true,
        title: translate("order"),
        formatComponent: (storageBox: StorageBox) => {
          if (storageBox && storageBox?.jobs) {
            return (
              <MoreOptions
                ref={ordersNumberMenu}
                title={translate("moreOrders")}
                manyOptionsToShow={2}
                data={storageBox?.jobs?.sort((orderA, orderB) => {
                  if (orderA?.reference && orderB?.reference) {
                    if (orderA?.reference?.toLowerCase() > orderB?.reference?.toLowerCase()) {
                      return 1;
                    }
                    if (orderA?.reference?.toLowerCase() < orderB?.reference?.toLowerCase()) {
                      return -1;
                    }
                  }

                  return 0;
                })}
                returnLabel={(job: Job) =>
                  `${job?.reference}${
                    job.sequence && Number(job.sequence) > 0 ? `/${job.sequence}` : ""
                  }`
                }
                asComponent={(label: string, job: Job) => (
                  <OrderOption
                    onClick={() => {
                      ordersNumberMenu?.current?.setAnchor(null);
                      orderViewComponentRef?.current?.open(Number(job.id));
                    }}
                  >
                    {label}
                  </OrderOption>
                )}
              />
            );
          }

          return null;
        },
      },
      {
        name: "storageBox",
        hideSearch: true,
        title: translate("status"),
        formatComponent: (storageBox: StorageBox) => (
          <BadgeLocationStatus
            value={
              storageBox?.jobs?.length && storageBox?.jobs?.length > 0
                ? LocationStatus.IN_USE
                : LocationStatus.EMPTY
            }
          />
        ),
      },
    ];

    return columns;
  };

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          color="#9CA6AF"
          lineHeight="18px"
          marginBottom="28px"
        >
          {translate("itFeelsEmptyRightNow")}
        </Typography>
      </EmptyDiv>
    );
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  return (
    <>
      <ShipeeziNewGrid
        loadingData={loadingLocations}
        emptyData={() => renderEmptyDiv()}
        data={locationsReports}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        columns={getColumns()}
        totalItems={total}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        positionActionsColumn="right"
        showSelection={false}
        generalActions={{
          orderBy: {
            right: ["downloadReport", "exportCSV"],
            left: ["handleSelectWarehouse", "handleSelectLocation", "handleFilterBy"],
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("storageBoxType"),
                column: "storageBoxType",
                options: storageBoxTypesList?.map((type) => type?.name ?? ""),
              },
              {
                name: translate("status"),
                column: "status",
                options: LOCATION_STATUS.map((storageStatus) => storageStatus?.name),
              },
            ],
            onChange: handleFilter,
          },
          downloadReport: renderDownloadReportButton(),
          handleSelectWarehouse: renderWarehouseSelect(),
          handleSelectLocation: renderLocationSelect(),
          exportCSV: renderExportCSVButton(),
        }}
      />

      <OrderView ref={orderViewComponentRef} />
    </>
  );
};
